import { ExternalLink, Globe, Image as ImageIcon } from 'lucide-react';
import type { LinkPreview } from '../../types/index';

interface LinkPreviewCardProps {
  linkPreview: LinkPreview;
  onClick?: () => void;
}

export default function LinkPreviewCard({ linkPreview, onClick }: LinkPreviewCardProps) {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      window.open(linkPreview.url, '_blank', 'noopener,noreferrer');
    }
  };

  const getDomainFromUrl = (url: string) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  return (
    <div
      onClick={handleClick}
      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 transition-colors cursor-pointer group"
    >
      {/* Image */}
      {linkPreview.image && (
        <div className="aspect-video bg-gray-100 dark:bg-gray-800 relative overflow-hidden">
          <img
            src={linkPreview.image}
            alt={linkPreview.title || 'Link preview'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
          
          {/* Overlay icon */}
          <div className="absolute top-2 right-2 bg-black bg-opacity-60 rounded-full p-1">
            <ExternalLink className="h-3 w-3 text-white" />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-3">
        {/* Title */}
        {linkPreview.title && (
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
            {linkPreview.title}
          </h3>
        )}

        {/* Description */}
        {linkPreview.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
            {linkPreview.description}
          </p>
        )}

        {/* Site info */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            {linkPreview.favicon ? (
              <img
                src={linkPreview.favicon}
                alt=""
                className="w-4 h-4 rounded"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <Globe className="h-3 w-3" />
            )}
            <span className="truncate">
              {linkPreview.siteName || getDomainFromUrl(linkPreview.url)}
            </span>
          </div>

          {/* Type badge */}
          {linkPreview.type && linkPreview.type !== 'website' && (
            <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs font-medium">
              {linkPreview.type.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {/* Fallback for links without image */}
      {!linkPreview.image && (
        <div className="flex items-center p-3 gap-3">
          <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
            <ImageIcon className="h-6 w-6 text-gray-400" />
          </div>
          
          <div className="flex-1 min-w-0">
            {linkPreview.title && (
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-1 mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                {linkPreview.title}
              </h3>
            )}
            
            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              {linkPreview.favicon ? (
                <img
                  src={linkPreview.favicon}
                  alt=""
                  className="w-3 h-3 rounded"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <Globe className="h-3 w-3" />
              )}
              <span className="truncate">
                {linkPreview.siteName || getDomainFromUrl(linkPreview.url)}
              </span>
            </div>
          </div>

          <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
        </div>
      )}
    </div>
  );
}
